// Live state for one BLE node (a parent or a group): the node row, its direct
// children, EVERY lamp beneath it, and every action the screen offers.
//
// A command to the node = the same packet to each lamp beneath it, in turn
// (core/ble/lamp/lampControl.sendToEach), each held HoldMs on the air. The
// counterpart of useLightChannels for a light that has no MQTT — nothing
// reports back, so "state" is the last values sent, kept on the server so
// every phone agrees.
//
// Counters advance locally FIRST, then RecordBleNodeCommand persists them and
// writes the audit rows. Single-flight: one loop at a time — the radio holds
// one advertisement, and a second press mid-loop would cancel the first.
//
// BLUETOOTH MESH NODES (Codec 'sig_mesh', Telink lamps) take the same actions
// through a different radio — core/ble/mesh/meshSession. A command to the node
// is ONE message to its group address; the mesh relays it. The sliders mean
// brightness / coolness there, not white / warm (see meshValues.js). Every
// public action below branches on `isMesh()`, so the screens call one API.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  addBleLamp, allocateMeshAddress, deleteBleNode, getBleNode, getBleNodeById, getMeshNetwork,
  recordBleNodeCommand, setBleNodeLock, updateBleNode,
} from '../api/bleNodes';
import { diagnoseAdvertiser } from '../ble/lamp/advertiser';
import {
  COMMANDS, canSend, identify, identifyEach, pairLamp,
  sendToEach, setChannelsEach, unpairLamp,
} from '../ble/lamp/lampControl';
import { isBleEnabled, withBluetoothOn } from '../ble/bleClient';
import { isNativePlatform } from '../platform';
import { openSession, elementWith, MODEL } from '../ble/mesh/meshSession';
import { scanMesh, UUID as MESH_UUID } from '../ble/mesh/meshGatt';
import { lastClientAddress } from '../ble/mesh/meshClientStore';
import { findHub, HubTransport, rememberHub } from '../ble/mesh/hubTransport';
import { HubMeshSession } from '../ble/mesh/hubSession';
import { noteNodeHub, watchHub } from '../ble/mesh/hubStatus';
import {
  commonRange, fromLightness, isMeshCodec, toCoolness, toKelvin, toLightness, validRange,
} from '../ble/mesh/meshValues';
import {
  formatLampState, isSwapped, mirrorCoolness, parseLampState, sliderValues, stateAfterOff, stateAfterOn, stateForValues,
} from '../ble/lampState';

const sleep = (ms) => new Promise((r) => { setTimeout(r, ms); });
/** How often an open screen re-reads the server for another phone's changes. */
const LIVE_REFRESH_MS = 4000;
/** Commands that OPERATE a lamp — what "last used" counts (same list as the server). */
const OPERATING = ['on', 'off', 'ctl', 'cww', 'night', 'identify'];
/** Identify: a FIXED number of blinks, so the right fixture is never missed. */
const BLINK_COUNT = 5;
/*
 * A blink is DARK long enough to be seen: off 1000 ms, on 700 ms, switched with
 * no fade. Seen 2026-10-11 — 400 ms each way, with the lamp's own fade, was not
 * recognisable as a blink at all.
 */
const BLINK_OFF_MS = 1000;
const BLINK_ON_MS = 700;
/** A mesh lamp's commands need its mesh address, which a locked row does not carry. */
const meshSendable = (row) => !!row && Number(row.CanSend) === 1 && Number(row.MeshAddress) > 0;

/**
 * ⚠️ "You do not have access to this device" is what the server says when the
 * DeviceID 100 row has NO BleNode behind it at all — its access check finds no
 * node and answers 403. A listed device is one this user is mapped to, so that
 * reply means an orphan record (BL85149340 lost its group in the tree
 * migration), never a real refusal: say what it is.
 */
export const isOrphanError = (message) => /do not have access|not set up as a lamp group/i.test(String(message || ''));

export function bleLoadErrorText(message) {
  if (isOrphanError(message)) {
    return 'Old Bluetooth record — no lamp group or lamps behind it. It can be removed.';
  }
  return message || 'Could not load this device';
}

export function useBleNode(uniqueNumber, userId) {
  const [node, setNode] = useState(null);
  const [ancestors, setAncestors] = useState([]);
  const [children, setChildren] = useState([]);
  const [lamps, setLamps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null); // { i, n, name } while looping
  const [status, setStatus] = useState('');
  const [failed, setFailed] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);

  const [meshLog, setMeshLog] = useState([]); // pairing / configure steps, for the dialog
  const [reading, setReading] = useState(false); // reading the lamps' real state
  const [via, setVia] = useState(null); // 'ble' | 'hub' — how the last command went
  const [hubUno, setHubUno] = useState(undefined); // the location's hub; null = none
  const [orphan, setOrphan] = useState(false); // a DeviceID 100 row with no BleNode behind it
  // { live, ready, lastLiveAt } — shared MESHPING, every minute (core/ble/mesh/hubStatus)
  const [hub, setHub] = useState({ live: undefined, ready: undefined, lastLiveAt: null });

  const nodeRef = useRef(null);
  const lampsRef = useRef([]);
  const inFlight = useRef(false);
  const readingRef = useRef(false);
  const sessionRef = useRef(null);
  const hubRef = useRef({ locationId: null, uno: undefined });
  const preferHubUntil = useRef(0);

  const setNodeBoth = (n) => { nodeRef.current = n; setNode(n); };
  const setLampsBoth = (l) => {
    lampsRef.current = l;
    setLamps(l);
    sessionRef.current?.setDevKeys(l);
  };
  const isMesh = () => isMeshCodec(nodeRef.current?.Codec);

  const reload = useCallback(async () => {
    if (!uniqueNumber || !userId) return;
    try {
      const res = await getBleNode(userId, uniqueNumber);
      // A server that knows the location's hub says so here — no slow lookup.
      // ⚠️ Only a FOUND hub is trusted. A null was once wrong everywhere (the
      // server filtered on DevicesInstalled.Status, which is the last reported
      // state, not "active"), and trusting it hid every hub-driven card; a null
      // now falls through to the app's own lookup (shared, cached 5 min).
      if (res.hub && res.node) {
        rememberHub(res.node.LocationID, res.hub);
        setHubUno(res.hub);
      }
      setNodeBoth(res.node);
      setAncestors(res.ancestors || []);
      setChildren(res.children || []);
      setLampsBoth(res.lamps || []);
      setError('');
    } catch (e) {
      setError(bleLoadErrorText(e?.message));
      setOrphan(isOrphanError(e?.message));
    } finally {
      setLoading(false);
    }
  }, [uniqueNumber, userId]);

  // Which radio layer is broken. A mesh node needs only Bluetooth on (GATT, any
  // platform); a LampSmart node needs the Android advertising plugin.
  const refreshDiagnostics = useCallback(async () => {
    if (isMeshCodec(nodeRef.current?.Codec)) {
      const native = isNativePlatform();
      const on = native ? await isBleEnabled() : false;
      // Bluetooth off is not a problem when the location's hub can do it.
      if (!on && await findHub(nodeRef.current?.LocationID, userId)) {
        setDiagnostics({
          supported: true, plugin: true, radioReady: true, permission: 'unknown', detail: '',
        });
        return;
      }
      setDiagnostics({
        supported: native,
        plugin: true,
        radioReady: on,
        permission: 'unknown',
        detail: native ? (on ? '' : 'Bluetooth is switched off.') : 'Bluetooth Mesh lamps are controlled from the phone app.',
      });
      return;
    }
    setDiagnostics(await diagnoseAdvertiser());
  }, [userId]);

  useEffect(() => {
    setLoading(true);
    reload().then(refreshDiagnostics);
  }, [reload, refreshDiagnostics]);

  // Which hub serves this node's location — the desktop shows its controls
  // only when there is one, and both shells say so.
  const nodeLocationId = node?.LocationID;
  useEffect(() => {
    if (!nodeLocationId) return undefined;
    let alive = true;
    // Instant when the server's reply carried `hub` (rememberHub); otherwise the
    // shared fallback lookup.
    findHub(nodeLocationId, userId).then((uno) => { if (alive) setHubUno(uno || null); });
    return () => { alive = false; };
  }, [nodeLocationId, userId]);

  // Is that hub LIVE? Only mesh nodes go through a hub. One MESHPING per hub
  // per minute however many cards watch it; every real command counts too.
  const meshNode = isMeshCodec(node?.Codec);
  useEffect(() => {
    if (!hubUno || !meshNode) return undefined;
    noteNodeHub(uniqueNumber, hubUno); // "Hide offline" reads it (useHideOffline)
    return watchHub(hubUno, userId, setHub);
  }, [hubUno, meshNode, userId, uniqueNumber]);

  // Leaving the screen drops the mesh link a few seconds later, unless the next
  // screen (another node at the same location) picks the same session up.
  useEffect(() => () => { sessionRef.current?.release(); }, []);

  // ⚠️ ANOTHER PHONE'S CHANGE MUST SHOW HERE WITHOUT REOPENING (seen 2026-10-10:
  // two phones on one mesh lamp, each screen kept its own last values until it
  // was closed). The lamp cannot tell us — it talks to one phone at a time and
  // this one is not connected while idle — but every phone records what it sent
  // (RecordBleNodeCommand -> LastValues), so an open, visible screen re-reads
  // the server every few seconds. Skipped while a command is in flight, so a
  // poll can never land between our send and our record and undo our own press.
  useEffect(() => {
    if (!uniqueNumber || !userId) return undefined;
    const sig = (res) => JSON.stringify([
      res?.node?.LastValues, res?.node?.Locked, res?.node?.Name,
      (res?.lamps || []).map((l) => [l.NodeId, l.LastValues, l.Locked, l.Name, l.MeshConfigured, l.SwapWarm, l.LastUsedAt]),
      (res?.children || []).map((c) => [c.NodeId, c.Name, c.Locked, c.LampCount]),
    ]);
    let last = null;
    const t = setInterval(async () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (inFlight.current) return;
      try {
        const res = await getBleNode(userId, uniqueNumber);
        if (inFlight.current) return; // a press started while we were fetching
        // Redraw only when the server's state moved since the last look.
        const s = sig(res);
        if (s === last) return;
        last = s;
        setNodeBoth(res.node);
        setAncestors(res.ancestors || []);
        setChildren(res.children || []);
        setLampsBoth(res.lamps || []);
      } catch (e) {
        // Offline for a moment: keep what is on screen.
      }
    }, LIVE_REFRESH_MS);
    return () => clearInterval(t);
  }, [uniqueNumber, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Radio settings come from the node being commanded.
  const radioOpts = () => ({
    includeFlags: Number(nodeRef.current?.IncludeFlags ?? 1) === 1,
    holdMs: Number(nodeRef.current?.HoldMs) || 300,
    onProgress: (i, n, lamp) => setProgress({ i: i + 1, n, name: lamp.Name }),
  });

  const patchNode = (patch) => setNodeBoth({ ...nodeRef.current, ...patch });
  const patchLamps = (updates) => {
    const byId = new Map(updates.map((u) => [u.NodeId, u]));
    setLampsBoth(lampsRef.current.map((l) => (byId.has(l.NodeId) ? { ...l, ...byId.get(l.NodeId) } : l)));
  };
  /** Which lamps a command from this screen targets: all, or one. */
  const targets = (lamp) => (lamp ? lampsRef.current.filter((l) => l.NodeId === lamp.NodeId) : lampsRef.current);

  const record = async (command, payload, result, updates) => {
    const n = nodeRef.current;
    if (!n) return;
    // "Last used" on the rows: this phone's press shows at once, as "you";
    // the server's time + name replace it on the next read.
    if (OPERATING.includes(command)) {
      const at = Date.now();
      patchLamps((updates || []).map((u) => ({ NodeId: u.NodeId, LastUsedLocalMs: at })));
      patchNode({ LastUsedLocalMs: at });
    }
    try {
      await recordBleNodeCommand(userId, n.NodeId, command, payload, result, updates);
    } catch (e) {
      // The lamps already reacted; a failed record must not undo that on screen.
    }
  };

  const run = useCallback(async (label, action) => {
    if (inFlight.current) {
      setFailed(true);
      setStatus('Still sending the last command');
      return false;
    }
    inFlight.current = true;
    setBusy(true);
    setProgress(null);
    try {
      // Bluetooth off -> Android's "turn on Bluetooth?" dialog, then the same
      // action again. Actions that never touch the radio never prompt.
      const result = await withBluetoothOn(action);
      setFailed(false);
      setStatus(label);
      return result === undefined ? true : result;
    } catch (e) {
      setFailed(true);
      setStatus(e?.message || String(e));
      refreshDiagnostics();
      return false;
    } finally {
      inFlight.current = false;
      setBusy(false);
      setProgress(null);
    }
  }, [refreshDiagnostics]);

  // ── Bluetooth Mesh plumbing ──────────────────────────────────────────────

  const meshLogLine = (s) => setMeshLog((l) => [...l.slice(-40), s]);

  /** The location's mesh session: keys from the server, this phone's own address. */
  const meshSession = async () => {
    const n = nodeRef.current;
    if (!n) throw new Error('Not loaded yet');
    if (sessionRef.current && sessionRef.current.locationId === n.LocationID) {
      sessionRef.current.log = meshLogLine;
      return sessionRef.current;
    }
    const res = await getMeshNetwork(userId, n.NodeId, lastClientAddress(n.LocationID));
    const s = openSession(res.network, meshLogLine);
    s.setDevKeys(lampsRef.current);
    sessionRef.current = s;
    return s;
  };

  /** BLE ids worth recognising while looking for a proxy: the lamps we know. */
  const meshProxyOpts = () => ({
    knownIds: lampsRef.current.map((l) => l.MeshMac).filter(Boolean),
    knownAddresses: lampsRef.current.map((l) => Number(l.MeshAddress)).filter(Boolean),
  });

  /** The location's hub (ESP32BLEGATEWAY), or null — looked up once per location. */
  const hubForNode = async () => {
    const loc = nodeRef.current?.LocationID;
    if (hubRef.current.locationId !== loc) hubRef.current = { locationId: loc, uno: await findHub(loc, userId) };
    return hubRef.current.uno;
  };

  /*
   * ⚠️ ONE CHOICE PER COMMAND: PHONE BLUETOOTH WHEN THE LAMPS ARE IN RANGE,
   * OTHERWISE THE HUB (docs/20). Bluetooth off, or no radio at all → the hub
   * straight away (no Bluetooth prompt when the hub can do it). Bluetooth on →
   * a SHORT scan for a lamp (4 s); none → the hub, and the hub stays preferred
   * for two minutes so a remote user does not wait 4 s on every press. With no
   * hub at the location, Bluetooth is the only way and its errors stand.
   */
  const HUB_PREFER_MS = 2 * 60 * 1000;
  const meshControl = async () => {
    const native = isNativePlatform();
    const btOn = native ? await isBleEnabled() : false;
    if (!btOn || Date.now() < preferHubUntil.current) {
      const hub = await hubForNode();
      if (hub) { setVia('hub'); return new HubTransport(hub, userId); }
      if (!native) throw new Error('No hub at this location — these lamps need the phone app near them, or a hub.');
    }
    const s = await meshSession();
    try {
      await s.ensureProxy({ ...meshProxyOpts(), scanMs: 4000 });
      preferHubUntil.current = 0;
      setVia('ble');
      return s;
    } catch (e) {
      const hub = await hubForNode();
      if (!hub) throw e;
      preferHubUntil.current = Date.now() + HUB_PREFER_MS;
      setVia('hub');
      return new HubTransport(hub, userId);
    }
  };

  /**
   * The radio for PAIRING, CONFIGURING and RESETTING — the same choice as
   * meshControl (phone Bluetooth near the lamps, else the hub). Through the hub
   * it is a HubMeshSession, which runs the phone's own configure / subscribe /
   * reset steps with the hub's radio (core/ble/mesh/hubSession.js).
   */
  const meshConfigSession = async () => {
    const s = await meshControl();
    return s.isHub ? new HubMeshSession(s.uno, userId, meshLogLine) : s;
  };

  /** Where a command goes: one lamp's address, or this node's group address. */
  const meshDst = (lamp, model) => {
    if (lamp) return (model != null ? elementWith(lamp, model) : null) ?? Number(lamp.MeshAddress);
    const g = Number(nodeRef.current?.MeshAddress);
    if (!g) throw new Error('This group has no mesh address yet — reopen it.');
    return g;
  };

  const meshTargets = (lamp) => {
    const list = targets(lamp);
    if (list.length === 0) throw new Error('No lamps here yet');
    const ok = list.filter(meshSendable);
    if (ok.length === 0) throw new Error('Every lamp here is locked');
    return ok;
  };

  /**
   * Persist + audit a mesh command. `next(raw)` turns each lamp's previous
   * LastValues into its new one (lampState.js), so On/Off keep the brightness.
   * No TxCount — the phone's sequence number replaces it.
   */
  const meshRecord = async (name, next, lamp) => {
    const updates = targets(lamp).map((l) => ({ NodeId: l.NodeId, LastValues: next(l.LastValues) }));
    const nodePayload = lamp ? updates[0]?.LastValues : next(nodeRef.current?.LastValues);
    patchLamps(updates);
    if (!lamp) patchNode({ LastValues: nodePayload });
    await record(name, nodePayload, 'sent', updates);
  };

  const MESH = { mesh: true };

  const meshOnOff = (on, lamp) => async () => {
    meshTargets(lamp);
    const s = await meshControl();
    await s.onOff(meshDst(lamp, MODEL.ONOFF), on, meshProxyOpts());
    await meshRecord(on ? 'on' : 'off', (raw) => (on ? stateAfterOn(raw, MESH) : stateAfterOff(raw, MESH)), lamp);
  };

  const meshSetValues = (brightness, coolness, lamp) => async () => {
    const list = meshTargets(lamp);
    const s = await meshControl();
    if (Number(brightness) <= 0) {
      // Brightness 0 (or the Off preset) IS off — and keeps the last brightness for the next On.
      await s.onOff(meshDst(lamp, MODEL.ONOFF), false, meshProxyOpts());
      await meshRecord('off', (raw) => stateAfterOff(raw, MESH), lamp);
      return;
    }
    const level = toLightness(brightness);
    const rangeOf = (l) => validRange({ min: l.MeshTempMin, max: l.MeshTempMax });
    if (lamp) {
      // One lamp: its own colour, mirrored if its LEDs are reversed.
      await s.ctl(meshDst(lamp, MODEL.CTL), level, toKelvin(mirrorCoolness(coolness, isSwapped(lamp)), rangeOf(lamp)), meshProxyOpts());
    } else {
      // A group: colour and level as SEPARATE messages, so reversed lamps can
      // take the mirrored colour from their own address while everyone takes
      // the same level. Colour first, so a lamp coming on comes on right.
      const range = commonRange(list);
      const g = meshDst(); // the node's group address; throws if it has none yet
      const sw = Number(nodeRef.current?.MeshSwapAddress) || 0;
      // Lamps the group colour cannot reach — no CTL Temperature server in their
      // composition, or reversed with no reversed address yet — get theirs directly.
      const direct = list.filter((l) => elementWith(l, MODEL.CTL_TEMP) == null || (isSwapped(l) && !sw));
      const viaGroup = list.filter((l) => !direct.includes(l));
      if (viaGroup.some((l) => !isSwapped(l))) await s.temperature(g, toKelvin(coolness, range), meshProxyOpts());
      if (viaGroup.some(isSwapped)) await s.temperature(sw, toKelvin(mirrorCoolness(coolness, true), range), meshProxyOpts());
      await s.lightness(g, level, meshProxyOpts());
      for (const l of direct) {
        // eslint-disable-next-line no-await-in-loop
        await s.ctl(meshDst(l, MODEL.CTL), level, toKelvin(mirrorCoolness(coolness, isSwapped(l)), rangeOf(l)), meshProxyOpts())
          .catch(() => {}); // one silent lamp must not fail the whole group
      }
    }
    await meshRecord('ctl', () => stateForValues(brightness, coolness, MESH), lamp);
  };

  /** What a lamp subscribes to: its groups, and where its COLOUR comes from. */
  const subsOf = (l) => ({ groups: l.MeshGroups || [], swapGroups: l.MeshSwapGroups || [], swapped: isSwapped(l) });

  /**
   * Blink to identify: exactly BLINK_COUNT blinks, ENDING IN THE STATE IT WAS
   * IN. One lamp: unacknowledged sets to its own address, steady rhythm. The
   * whole node: the same to its group address — every lamp together — then
   * the lamps that were off (when the group was mixed) are switched off again.
   */
  const meshBlink = (lamp) => async () => {
    const list = meshTargets(lamp);
    const s = await meshControl();
    const wasOn = (l) => parseLampState(l.LastValues, MESH)?.on ?? true;
    const allOff = list.every((l) => !wasOn(l));
    const pair = allOff ? [true, false] : [false, true];
    const dst = meshDst(lamp, MODEL.ONOFF);
    // Through the hub the whole blink is ONE command — the hub keeps the rhythm.
    for (let i = 0; i < (s.isHub ? 0 : BLINK_COUNT); i += 1) {
      for (const on of pair) {
        // eslint-disable-next-line no-await-in-loop
        await s.onOff(dst, on, { ...meshProxyOpts(), ack: false, instant: true });
        // eslint-disable-next-line no-await-in-loop
        await sleep(on ? BLINK_ON_MS : BLINK_OFF_MS);
      }
    }
    if (s.isHub) await s.blink(dst, BLINK_COUNT, BLINK_OFF_MS, BLINK_ON_MS, !allOff);
    if (!lamp && !allOff) {
      for (const l of list.filter((x) => !wasOn(x))) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await s.onOff(meshDst(l, MODEL.ONOFF), false, { ...meshProxyOpts(), ack: false });
        } catch (e) {
          // One lamp left on after a blink is not worth failing the blink for.
        }
      }
    }
    await record('identify', `${BLINK_COUNT}x`, 'sent', targets(lamp).map((l) => ({ NodeId: l.NodeId })));
  };

  /**
   * Bluetooth Mesh: ask every lamp what it is ACTUALLY doing (on/off, level,
   * colour) and correct the screen and the server where it differs — a lamp
   * switched at the wall, or changed while no phone was looking. Background
   * work: never prompts for Bluetooth, never raises an error dialog, skipped
   * while a command is running. Near the lamps over Bluetooth, otherwise
   * through the location's hub (MESHGET).
   */
  const readLampStates = useCallback(async () => {
    if (!isMeshCodec(nodeRef.current?.Codec) || readingRef.current || inFlight.current) return 0;
    const list = lampsRef.current.filter(meshSendable);
    if (!list.length) return 0;
    readingRef.current = true;
    setReading(true);
    try {
      // Phone Bluetooth if a lamp is in range, else the hub (meshControl).
      const s = await meshControl();
      const changed = [];
      for (const l of list) {
        // eslint-disable-next-line no-await-in-loop
        const r = await s.readState(l).catch(() => null);
        if (!r) continue; // eslint-disable-line no-continue
        const prev = parseLampState(l.LastValues, MESH);
        const range = validRange({ min: l.MeshTempMin, max: l.MeshTempMax });
        // An OFF lamp reports lightness 0: keep the level it will come back at.
        const level = r.lightness != null && r.on ? fromLightness(r.lightness) : (prev?.a ?? 255);
        // A reversed lamp REPORTS the mirrored colour: mirror it back to what is seen.
        const cool = r.kelvin != null ? mirrorCoolness(toCoolness(r.kelvin, range), isSwapped(l)) : (prev?.b ?? 128);
        const next = formatLampState(level || prev?.a || 255, cool, r.on);
        if (next !== l.LastValues) changed.push({ NodeId: l.NodeId, LastValues: next });
      }
      if (changed.length) {
        patchLamps(changed);
        // Recorded so every other phone sees the corrected state too.
        await recordBleNodeCommand(userId, nodeRef.current.NodeId, 'state', null, 'read', changed).catch(() => {});
      }
      return changed.length;
    } catch (e) {
      return 0; // not near the lamps, or another phone holds them — try again later
    } finally {
      readingRef.current = false;
      setReading(false);
    }
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Configure a paired mesh lamp and save what it reported. */
  const meshConfigure = async (s, lamp, preferIds = []) => {
    const out = await s.configure(lamp, subsOf(lamp), { preferIds, ...meshProxyOpts() });
    const patch = {
      MeshComposition: JSON.stringify(out.composition),
      MeshConfigured: 1,
      ...(out.tempRange ? { MeshTempMin: out.tempRange.min, MeshTempMax: out.tempRange.max } : {}),
    };
    await updateBleNode(userId, lamp.NodeId, patch);
    return { ...lamp, ...patch };
  };

  /**
   * After a group moves to another parent (or out of one), every lamp in it
   * must listen on the new set of group addresses — or the OLD parent keeps
   * controlling it. Needs each lamp's device key (owner or pairer).
   */
  const meshResubscribeGroup = async (groupNodeId) => {
    const res = await getBleNodeById(userId, groupNodeId);
    const groupLamps = (res.lamps || []).filter(meshSendable);
    if (groupLamps.length === 0) return 0;
    let s;
    try {
      // Phone Bluetooth near the lamps, else the hub.
      s = await meshConfigSession();
    } catch (e) {
      // Neither (no hub, not near): the move is saved; every lamp still needs
      // "Configure again" later.
      return groupLamps.length;
    }
    s.setDevKeys(groupLamps);
    let missed = 0;
    for (const l of groupLamps) {
      if (!l.MeshDevKey) { missed += 1; continue; } // eslint-disable-line no-continue
      try {
        // eslint-disable-next-line no-await-in-loop
        await s.subscribe(l, subsOf(l));
      } catch (e) {
        missed += 1;
      }
    }
    return missed;
  };

  // ── commands ─────────────────────────────────────────────────────────────
  // `lamp` undefined = everything under this node; a lamp row = that lamp only.

  /** `next(raw)` = each lamp's new LastValues from its previous one (lampState.js). */
  const sendCommand = (name, cmd, next, lamp) => async () => {
    const list = targets(lamp);
    if (list.length === 0) throw new Error('No lamps here yet');
    const sent = await sendToEach(list, cmd, radioOpts());
    if (sent.length === 0) throw new Error('Every lamp here is locked');
    const prevById = new Map(list.map((l) => [l.NodeId, l.LastValues]));
    const updates = sent.map((x) => ({ ...x, LastValues: next(prevById.get(x.NodeId)) }));
    const nodePayload = lamp ? updates[0]?.LastValues : next(nodeRef.current?.LastValues);
    patchLamps(updates);
    if (!lamp) patchNode({ LastValues: nodePayload });
    await record(name, nodePayload, 'sent', updates);
  };

  const label = (lamp, what) => (lamp ? `${lamp.Name} ${what}` : `${nodeRef.current?.Name || 'All'} ${what}`);

  const turnOn = (lamp) => run(label(lamp, 'on'), isMesh()
    ? meshOnOff(true, lamp)
    : sendCommand('on', COMMANDS.on, (raw) => stateAfterOn(raw), lamp));
  const turnOff = (lamp) => run(label(lamp, 'off'), isMesh()
    ? meshOnOff(false, lamp)
    : sendCommand('off', COMMANDS.off, (raw) => stateAfterOff(raw), lamp));
  // Mesh has no night opcode: a dim, warmest light is what the LampSmart one does.
  const nightMode = (lamp) => run(label(lamp, 'night mode'), isMesh()
    ? meshSetValues(20, 0, lamp)
    : sendCommand('night', COMMANDS.night, (raw) => stateAfterOn(raw), lamp));

  // Mesh node: (brightness, coolness). LampSmart node: (white, warm).
  const setValues = (white, warm, lamp) => run(label(lamp, 'updated'), isMesh() ? meshSetValues(white, warm, lamp) : async () => {
    const list = targets(lamp);
    if (list.length === 0) throw new Error('No lamps here yet');
    const updates = await setChannelsEach(list, white, warm, radioOpts());
    if (updates.length === 0) throw new Error('Every lamp here is locked');
    patchLamps(updates);
    if (!lamp) patchNode({ LastValues: stateForValues(white, warm) });
    await record('cww', stateForValues(white, warm), 'sent', updates);
  });

  /** Blink to identify, BLINK_COUNT times, ending as it was: one lamp, or every lamp. */
  const blink = (lamp) => run(label(lamp, 'blinked'), isMesh() ? meshBlink(lamp) : async () => {
    if (lamp) {
      const cur = targets(lamp)[0] || lamp;
      const tx = await identify(cur, radioOpts());
      patchLamps([{ NodeId: cur.NodeId, TxCount: tx }]);
      await record('identify', null, 'sent', [{ NodeId: cur.NodeId, TxCount: tx }]);
      return;
    }
    const sent = await identifyEach(lampsRef.current, radioOpts());
    if (sent.length === 0) throw new Error('No lamps here yet');
    patchLamps(sent);
    await record('identify', null, 'sent', sent);
  });

  // ── membership ───────────────────────────────────────────────────────────

  /**
   * Add a lamp under a group: reserve an identity on the server, then pair.
   * The user must have power-cycled ONLY this lamp just before pressing. If
   * the radio fails the reservation is released again.
   */
  const addLamp = (groupNodeId, name, roomId) => run('Pairing sent. Watch the lamp.', async () => {
    const res = await addBleLamp(userId, groupNodeId, name, roomId);
    const lamp = res.lamp;
    try {
      const tx = await pairLamp(lamp, radioOpts());
      const paired = { ...lamp, TxCount: tx, LastValues: null };
      setLampsBoth([...lampsRef.current, paired]);
      if (groupNodeId === nodeRef.current?.NodeId) setChildren((c) => [...c, paired]);
      await record('pair', null, 'sent', [{ NodeId: lamp.NodeId, TxCount: tx }]);
    } catch (e) {
      await deleteBleNode(userId, lamp.NodeId).catch(() => {});
      throw e;
    }
  });

  /**
   * Bluetooth Mesh: scan for lamps waiting to be paired (factory-reset, 0x1827).
   * `onDevice({ deviceId, name, rssi, uuid })` per sighting. -> stop()
   */
  const scanForMeshLamps = async (onDevice) => {
    // No Bluetooth here (the desktop) or Bluetooth off: the HUB listens for
    // lamps waiting to be paired, and pairs them (hub firmware 1.1.0+).
    const native = isNativePlatform();
    const btOn = native ? await isBleEnabled() : false;
    if (!btOn) {
      const hub = await hubForNode();
      if (hub) {
        setVia('hub');
        return new HubMeshSession(hub, userId, meshLogLine).scan(onDevice);
      }
      if (!native) throw new Error('No hub at this location — lamps are paired from the phone app, near them.');
    }
    return withBluetoothOn(() => scanMesh((d) => {
      if (d.kind === 'unprovisioned') onDevice(d);
    }, { timeoutMs: 20000, services: [MESH_UUID.PROV_SERVICE] }));
  };

  /**
   * Bluetooth Mesh: pair ONE `device` (from scanForMeshLamps) into the group,
   * save it, then configure it onto the group addresses. Pairing comes FIRST —
   * the device key only exists afterwards — and the row is saved immediately
   * after, because a paired lamp whose key is lost can only be recovered by a
   * factory reset at the wall. A configure failure keeps the lamp; "Configure
   * again" finishes the job.
   * -> { lamp, movedFrom, configureError? }; throws if pairing or saving failed.
   */
  const pairOneMesh = async (s, groupNodeId, device, name, roomId) => {
    const paired = await s.pair(device, async (count) => (await allocateMeshAddress(userId, groupNodeId, count)).address);
    const meshFields = {
      MeshAddress: paired.address,
      MeshElements: paired.numElements,
      MeshDevKey: paired.devKey,
      MeshUuid: device.uuid,
      // The BLE id a PHONE saw; the hub has none to give (server keeps the old).
      ...(device.viaHub ? {} : { MeshMac: device.deviceId }),
    };
    let saved = null;
    let movedFrom = null;
    let saveError = null;
    for (let i = 0; i < 3 && !saved; i += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await addBleLamp(userId, groupNodeId, name, roomId, meshFields);
        saved = res.lamp;
        // The server re-uses the chip's own row if it was ever paired before.
        movedFrom = res.movedFrom || null;
      } catch (e) {
        saveError = e;
        // eslint-disable-next-line no-await-in-loop
        await sleep(1500);
      }
    }
    if (!saved) {
      throw new Error(`paired but could not be saved (${saveError?.message}). Factory-reset it at the wall and add it again.`);
    }
    s.setDevKeys([saved]);
    meshLogLine(`${saved.Name}: saved, waiting for it to restart…`);
    await sleep(2500);
    let configureError = null;
    try {
      await meshConfigure(s, saved, [device.deviceId]);
    } catch (e) {
      configureError = e.message;
    }
    await record('pair', `0x${paired.address.toString(16)}`, configureError ? 'unconfigured' : 'ok', [{ NodeId: saved.NodeId }]);
    return { lamp: saved, movedFrom, configureError };
  };

  /**
   * Bluetooth Mesh: pair SEVERAL lamps in one go — one after another, with
   * progress. Safe for mesh because each pairing is addressed to one chip
   * (LampSmart pairing is not, and stays one at a time). A failure does not
   * stop the rest. `name` applies only when a single lamp is paired; several
   * are named by the server (wwh<n>).
   * -> { added: [{lamp, movedFrom, configureError}], failed: [{device, error}] }
   */
  const addMeshLamps = (groupNodeId, devices, name, roomId) => run(
    devices.length === 1 ? 'Lamp added' : 'Lamps added',
    async () => {
      setMeshLog([]);
      // Each lamp is paired by the radio that FOUND it: this phone, or the hub.
      const phone = devices.some((d) => !d.viaHub) ? await meshSession() : null;
      let hubS = null;
      if (devices.some((d) => d.viaHub)) {
        const hub = await hubForNode();
        if (!hub) throw new Error('This location has no hub to pair through.');
        hubS = new HubMeshSession(hub, userId, meshLogLine);
      }
      const added = [];
      const failed = [];
      for (let i = 0; i < devices.length; i += 1) {
        const d = devices[i];
        const s = d.viaHub ? hubS : phone;
        setProgress({ i: i + 1, n: devices.length, name: d.name || d.deviceId, verb: 'Pairing' });
        meshLogLine(`── ${i + 1}/${devices.length}: ${d.deviceId}`);
        try {
          // eslint-disable-next-line no-await-in-loop
          added.push(await pairOneMesh(s, groupNodeId, d, devices.length === 1 ? name : '', roomId));
        } catch (e) {
          failed.push({ device: d, error: e.message });
          meshLogLine(`✗ ${d.deviceId}: ${e.message}`);
        }
      }
      await reload();
      if (added.length === 0) throw new Error(failed[0]?.error || 'No lamp was paired');
      return { added, failed };
    },
  );

  /** One lamp — kept for the single-pick path; same as addMeshLamps with one device. */
  const addMeshLamp = (groupNodeId, device, name, roomId) => addMeshLamps(groupNodeId, [device], name, roomId);

  /** Bluetooth Mesh: re-run configure (app key, binds, group subscriptions, range). */
  const reconfigure = (lamp) => run(`${lamp.Name} configured`, async () => {
    setMeshLog([]);
    const cur = targets(lamp)[0] || lamp;
    if (!cur.MeshDevKey) throw new Error('Only the owner, or whoever paired this lamp, can configure it.');
    const s = await meshConfigSession();
    s.setDevKeys([cur]);
    const done = await meshConfigure(s, cur);
    patchLamps([done]);
  });

  /** Re-send pairing for an existing lamp (it did not take, or was replaced). */
  const rePair = (lamp) => run(`${lamp.Name}: pairing sent again`, async () => {
    const cur = targets(lamp)[0] || lamp;
    const tx = await pairLamp(cur, radioOpts());
    patchLamps([{ NodeId: cur.NodeId, TxCount: tx }]);
    await record('pair', null, 'sent', [{ NodeId: cur.NodeId, TxCount: tx }]);
  });

  /**
   * The radio half of removing a lamp: Telink -> factory reset over the mesh
   * (the chip is "new from the company" again, ready for any group); LampSmart
   * -> the unpair packet. -> null, or the Error if the lamp did not take it.
   */
  const unpairRadio = async (cur) => {
    try {
      if (isMesh()) {
        if (meshSendable(cur) && cur.MeshDevKey) {
          // Phone Bluetooth near the lamp, else the hub (it is given the
          // lamp's device key for this one command — hubSession.js).
          const s = await meshConfigSession();
          s.setDevKeys([cur]);
          await s.reset(cur, meshProxyOpts());
        } else if (meshSendable(cur)) {
          throw new Error('only the owner or whoever paired it can reset it — reset it at the wall');
        }
      } else if (!isNativePlatform()) {
        // LampSmart: the unpair packet is advertised by the PHONE; no hub path yet.
        throw new Error('LampSmart lamps are unpaired from the phone app, near them');
      } else if (canSend(cur)) {
        await unpairLamp(cur, radioOpts());
      }
      return null;
    } catch (e) {
      return e;
    }
  };

  /**
   * Unmap EVERY lamp under this group at once: reset each chip, then take its
   * row out of the group. ⚠️ The row is KEPT in the database (Status 0) — the
   * chip id is its UniqueNumber, and pairing it again later, into any group for
   * any user, re-uses that row. A lamp that does not answer is still taken out,
   * and named in the result: it still holds this network's keys until it is
   * reset at the wall. -> { removed: n, notReset: [names] }
   */
  const removeAllLamps = () => run('All lamps removed', async () => {
    const list = lampsRef.current.filter((l) => l.ParentNodeId === nodeRef.current?.NodeId);
    if (list.length === 0) throw new Error('No lamps in this group');
    const notReset = [];
    let removed = 0;
    for (let i = 0; i < list.length; i += 1) {
      const l = list[i];
      setProgress({ i: i + 1, n: list.length, name: l.Name, verb: 'Removing' });
      // eslint-disable-next-line no-await-in-loop
      const err = await unpairRadio(l);
      if (err) notReset.push(l.Name);
      try {
        // eslint-disable-next-line no-await-in-loop
        await deleteBleNode(userId, l.NodeId);
        removed += 1;
      } catch (e) {
        notReset.push(`${l.Name} (not removed: ${e.message})`);
      }
    }
    await reload();
    return { removed, notReset };
  });

  /** Unpair, then drop the row — even if the radio failed, so a dead lamp can be cleaned up. */
  const removeLamp = (lamp) => run(`${lamp.Name} removed`, async () => {
    const cur = targets(lamp)[0] || lamp;
    const radioError = await unpairRadio(cur);
    await deleteBleNode(userId, cur.NodeId);
    setLampsBoth(lampsRef.current.filter((l) => l.NodeId !== cur.NodeId));
    setChildren((c) => c.filter((x) => x.NodeId !== cur.NodeId));
    if (radioError) {
      throw new Error(isMesh()
        ? `${cur.Name} was removed from the app, but the lamp was not reset (${radioError.message}). It still holds this network's keys.`
        : `${cur.Name} was removed from the app, but the unpair packet failed: ${radioError.message}`);
    }
  });

  /**
   * Mark which lamps have REVERSED warm/cool LEDs (only some of a batch) — the
   * ticked set in the group's "Reverse white / warm white" list. Saved per lamp
   * (BleNode.SwapWarm); from then on the app sends those lamps the mirrored
   * colour. A Telink lamp must also move its colour subscription to the
   * reversed address, which needs Bluetooth near it and its device key (owner
   * or pairer); a LampSmart lamp needs nothing on the air.
   * -> { changed, notUpdated: [names] }
   */
  const setReversed = (reversedIds) => run('Reversed lamps saved', async () => {
    const want = new Set(reversedIds);
    const changes = lampsRef.current.filter((l) => (want.has(l.NodeId) ? 1 : 0) !== (isSwapped(l) ? 1 : 0));
    if (changes.length === 0) return { changed: 0, notUpdated: [] };
    for (const l of changes) {
      // eslint-disable-next-line no-await-in-loop
      await updateBleNode(userId, l.NodeId, { SwapWarm: want.has(l.NodeId) ? 1 : 0 });
    }
    // Fresh rows: the flag, and the reversed group addresses (allocated on read).
    await reload();
    const notUpdated = [];
    if (isMesh()) {
      const ids = new Set(changes.map((l) => l.NodeId));
      const fresh = lampsRef.current.filter((l) => ids.has(l.NodeId));
      const reachable = fresh.filter((l) => meshSendable(l) && l.MeshDevKey);
      fresh.filter((l) => !reachable.includes(l)).forEach((l) => notUpdated.push(l.Name));
      if (reachable.length) {
        const s = await meshConfigSession();
        s.setDevKeys(reachable);
        try {
          await s.ensureProxy(meshProxyOpts());
          for (const l of reachable) {
            setProgress({ i: reachable.indexOf(l) + 1, n: reachable.length, name: l.Name, verb: 'Updating' });
            try {
              // eslint-disable-next-line no-await-in-loop
              await s.subscribe(l, subsOf(l));
            } catch (e) {
              notUpdated.push(l.Name);
            }
          }
        } catch (e) {
          reachable.forEach((l) => notUpdated.push(l.Name));
        }
      }
    }
    return { changed: changes.length, notUpdated };
  });

  /** Remove an empty child group/parent (its DeviceID 100 row leaves the room). */
  const removeChildNode = (child) => run(`${child.Name} removed`, async () => {
    await deleteBleNode(userId, child.NodeId, { isDevice: true });
    setChildren((c) => c.filter((x) => x.NodeId !== child.NodeId));
  });

  const rename = async (target, name) => {
    const clean = String(name || '').trim();
    if (!clean) return;
    await updateBleNode(userId, target.NodeId, { Name: clean });
    if (target.NodeId === nodeRef.current?.NodeId) patchNode({ Name: clean });
    patchLamps([{ NodeId: target.NodeId, Name: clean }]);
    setChildren((c) => c.map((x) => (x.NodeId === target.NodeId ? { ...x, Name: clean } : x)));
  };

  const moveToRoom = async (target, roomId) => {
    await updateBleNode(userId, target.NodeId, { RoomID: roomId });
    if (target.NodeId === nodeRef.current?.NodeId) patchNode({ RoomID: roomId });
    patchLamps([{ NodeId: target.NodeId, RoomID: roomId }]);
    setChildren((c) => c.map((x) => (x.NodeId === target.NodeId ? { ...x, RoomID: roomId } : x)));
  };

  /** Put an existing group under THIS node (or detach with null). */
  const adoptGroup = (groupNodeId, parentNodeId) => run('Group moved', async () => {
    await updateBleNode(userId, groupNodeId, { ParentNodeId: parentNodeId });
    if (isMesh()) {
      // The lamps must now listen on the new parent's group address (and stop
      // listening on the old one), or the move is only on paper.
      const missed = await meshResubscribeGroup(groupNodeId);
      await reload();
      if (missed) throw new Error(`Group moved, but ${missed} lamp(s) could not be updated — the owner should open the group and use "Configure again" on them.`);
      return;
    }
    await reload();
  });

  // ── settings / security ──────────────────────────────────────────────────

  /** Owner only; covers the subtree. Reloads so withheld identities vanish. */
  const setLock = (target, locked) => run(locked ? 'Locked' : 'Unlocked', async () => {
    await setBleNodeLock(userId, target.NodeId, locked);
    await reload();
  });

  /** Encoding for this node and every lamp beneath it. No re-pair needed. */
  const setCodec = (codecId) => run(`Encoding set to ${codecId}`, async () => {
    await updateBleNode(userId, nodeRef.current.NodeId, { Codec: codecId });
    await reload();
  });

  const setIncludeFlags = (on) => run(on ? 'Flags header on' : 'Flags header off', async () => {
    await updateBleNode(userId, nodeRef.current.NodeId, { IncludeFlags: on ? 1 : 0 });
    patchNode({ IncludeFlags: on ? 1 : 0 });
  });

  const setHoldMs = (ms) => run(`Hold ${ms} ms per lamp`, async () => {
    await updateBleNode(userId, nodeRef.current.NodeId, { HoldMs: ms });
    patchNode({ HoldMs: ms });
  });

  return {
    node,
    ancestors,
    children,
    lamps,
    loading,
    error,
    reload,
    busy,
    progress,
    status,
    failed,
    diagnostics,
    refreshDiagnostics,
    isParent: node?.NodeType === 'parent',
    isOwner: Number(node?.IsMaster) === 1,
    isMesh: isMeshCodec(node?.Codec),
    // What the sliders show: an Off moves brightness to 0 (lampState.sliderValues).
    nodeValues: sliderValues(node?.LastValues, { mesh: isMeshCodec(node?.Codec) }),
    lampValues: (lamp) => sliderValues(lamp?.LastValues, { mesh: isMeshCodec(lamp?.Codec) }),
    canSendAny: isMeshCodec(node?.Codec) ? lamps.some(meshSendable) : lamps.some(canSend),
    meshLog,
    via,
    hubUno,
    /** No lamp group behind this device row at all — OrphanBleRemove offers to remove it. */
    orphan,
    /** true / false / undefined (not checked yet). */
    hubLive: hubUno ? hub.live : false,
    /** false = live but without this location's mesh keys (MESHNET:NONE). */
    hubReady: hub.ready,
    hubLastLiveAt: hub.lastLiveAt,
    reading,
    readLampStates,
    lampState: (lamp) => parseLampState(lamp?.LastValues, { mesh: isMeshCodec(lamp?.Codec) }),
    turnOn,
    turnOff,
    nightMode,
    setValues,
    blink,
    addLamp,
    scanForMeshLamps,
    addMeshLamp,
    addMeshLamps,
    removeAllLamps,
    setReversed,
    /** One lamp's reversed flag (its ⋮ menu) — the same save as the list. */
    setLampReversed: (lamp, on) => setReversed([
      ...lampsRef.current.filter((l) => isSwapped(l) && l.NodeId !== lamp.NodeId).map((l) => l.NodeId),
      ...(on ? [lamp.NodeId] : []),
    ]),
    reconfigure,
    rePair,
    removeLamp,
    removeChildNode,
    rename,
    moveToRoom,
    adoptGroup,
    setLock,
    setCodec,
    setIncludeFlags,
    setHoldMs,
  };
}
